/**
 * Shared redaction for anything that leaves the app as text.
 *
 * S116. `sentry.ts` has scrubbed emails and API keys out of Sentry events since
 * it was written, on the stated grounds that "this is a children's platform, so
 * we must NOT ship student PII to a third party". The console was never held to
 * the same standard: `logger.warn` and `logger.error` print in production, with
 * no redaction of any kind, so a model API key, a student's email address, a
 * password inside a rejected-credential object and a student uid all reached a
 * console that anybody can reopen — on the shared classroom Chromebook this
 * product is sold for, that is the device the NEXT child uses.
 *
 * The patterns live here so both paths use one list.
 */

/**
 * Extensions that make an "@" string a FILENAME rather than an address.
 *
 * S136, standing rule 7c: `sprite@2x.png` matched the email pattern — local
 * part "sprite", domain "2x.png", TLD "png" — so an error about a missing
 * retina asset reached Sentry as "Failed to load [redacted]", destroying the one
 * detail an engineer needed. Over-blocking rather than a leak, but it is the
 * shape rule 7c exists for, and it meant the filter had never been tested
 * against non-email text containing '@'.
 */
const FILE_EXTENSIONS =
  'png|jpe?g|gif|svg|webp|avif|ico|bmp|css|less|scss|js|jsx|ts|tsx|mjs|cjs|json|map|html?|xml|txt|md|csv|pdf|zip|gz|woff2?|ttf|otf|eot|mp[34]|wav|ogg|webm|mov';

/** Text shapes that must never be printed or transmitted. */
export const SENSITIVE_PATTERNS: RegExp[] = [
  // emails — but not `name@2x.png` and friends, see FILE_EXTENSIONS.
  new RegExp(
    `[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\\.(?!(?:${FILE_EXTENSIONS})\\b)[a-zA-Z]{2,}`,
    'g'
  ),
  // Anthropic / OpenAI keys. S136: `\b` on the left, because without it the
  // "sk-" inside an ordinary word starts a match — `task-list-item-not-found-in-classroom`
  // was redacted to `ta[redacted]`, mangling the message mid-word.
  /\bsk-[a-zA-Z0-9_-]{20,}/g,
  /\bAIza[0-9A-Za-z_-]{20,}/g, // Google API keys
  /\beyJ[A-Za-z0-9_-]{10,}\.[A-Za-z0-9_-]{10,}\.[A-Za-z0-9_-]{10,}/g, // JWTs
];

/**
 * Object keys whose VALUE is sensitive whatever it looks like. A password is
 * just a string; no pattern can recognise one.
 */
const SENSITIVE_KEY = /pass(word|phrase)?|secret|token|api[-_]?key|credential|authorization|cookie|session[-_]?id|private[-_]?key/i;

export const REDACTED = '[redacted]';

/** Replace every sensitive shape in a string. */
export const scrubText = (text: string): string => {
  let out = text;
  for (const pattern of SENSITIVE_PATTERNS) {
    // The patterns are /g, so lastIndex has to be reset between uses.
    pattern.lastIndex = 0;
    out = out.replace(pattern, REDACTED);
  }
  return out;
};

/**
 * A deep copy of `value` with sensitive strings and sensitive keys redacted.
 *
 * Never throws: a value that cannot be walked (a getter that throws, a proxy, a
 * cyclic graph) comes back as a placeholder rather than taking the caller down
 * with it — the caller is usually a `catch` block, and losing the original error
 * to a redaction failure would be worse than the leak it prevents.
 */
export const redact = (value: unknown, seen = new WeakSet<object>()): unknown => {
  try {
    if (typeof value === 'string') return scrubText(value);
    if (value === null || typeof value !== 'object') return value;

    if (seen.has(value as object)) return '[circular]';
    seen.add(value as object);

    if (value instanceof Error) {
      // An Error does not survive a spread: message and stack are non-enumerable.
      const copy = new Error(scrubText(value.message));
      copy.name = value.name;
      if (value.stack) copy.stack = scrubText(value.stack);
      return copy;
    }

    if (Array.isArray(value)) return value.map((item) => redact(item, seen));

    const out: Record<string, unknown> = {};
    for (const [key, item] of Object.entries(value as Record<string, unknown>)) {
      out[key] = SENSITIVE_KEY.test(key) ? REDACTED : redact(item, seen);
    }
    return out;
  } catch {
    return '[unserializable]';
  }
};
